import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

/**
 * Handles the email-verification link: /verify-email?token=...
 * Calls the backend to activate the account; on success the backend returns a
 * login JWT (via AuthContext.verifyEmail) so the user is signed in and sent to
 * the strategy page. On failure we show a clear message with a path back.
 */
const VerifyEmail = () => {
  const { verifyEmail } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [error, setError] = useState('');
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // guard StrictMode double-invoke
    ran.current = true;

    const token = params.get('token');
    if (!token) {
      setStatus('error');
      setError('This verification link is missing its token.');
      return;
    }

    (async () => {
      const result = await verifyEmail(token);
      if (result.success) {
        setStatus('success');
        setTimeout(() => navigate('/strategy', { replace: true }), 1500);
      } else {
        setStatus('error');
        setError(result.error || 'Verification failed.');
      }
    })();
  }, [params, verifyEmail, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full text-center space-y-6">
        {status === 'verifying' && (
          <>
            <LoadingSpinner size="lg" />
            <p className="text-gray-600">Verifying your email…</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="mx-auto h-12 w-12 bg-green-600 rounded-full flex items-center justify-center">
              <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Email verified!</h2>
            <p className="text-gray-600">Your account is active. Taking you to your dashboard…</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="mx-auto h-12 w-12 bg-red-600 rounded-full flex items-center justify-center">
              <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Verification failed</h2>
            <p className="text-gray-600">{error}</p>
            <div className="flex items-center justify-center gap-3">
              <Link to="/register" className="btn-secondary">Register again</Link>
              <Link to="/login" className="btn-primary">Go to Sign In</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
